import {FC, useState, useEffect} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {KTIcon} from '../../../../_metronic/helpers'
import {get_subproject_by_id} from '../api/subprojectsApi'
import {toast} from 'react-toastify'
import {LoadingProgressBar} from './LoadingProgressBar'
import {ProjectDetailSkeleton} from './ProjectDetailSkeleton'
import {Badge, Card, Button} from 'react-bootstrap'
import AssetDownloadModal from '../../digital-assets/components/AssetDownloadModal'
import {Asset, AssetCategory} from '../../digital-assets/types/assetTypes'
import {permissionCheck} from '../../../helper-functions/PermissionCheck'

const SubprojectDetail: FC = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const [subproject, setSubproject] = useState<any | null>(null)
  const [project, setProject] = useState<any | null>(null)
  const [uploads, setUploads] = useState<any[]>([])
  const [totalUploadCount, setTotalUploadCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState(true)
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)
  const canViewAssets = Boolean(permissionCheck('upload-view'))
  const canDownloadAsset = Boolean(permissionCheck('download-view'))

  useEffect(() => {
    if (id) {
      fetchSubprojectDetail()
    }
  }, [id])

  const fetchSubprojectDetail = async () => {
    try {
      setIsLoading(true)
      const response = await get_subproject_by_id(id || '')
      
      // API response structure: { status: true, message: "...", data: { subproject: {...} } }
      const apiSubproject = response?.data?.subproject || null
      
      if (apiSubproject) {
        setSubproject(apiSubproject)
        
        // Extract project from subproject.project (already included in API response)
        if (apiSubproject.project) {
          setProject(apiSubproject.project)
        }
        
        // Extract uploads array
        if (apiSubproject.upload && Array.isArray(apiSubproject.upload)) {
          setUploads(apiSubproject.upload)
        } else {
          setUploads([])
        }
        
        // Extract total upload count
        setTotalUploadCount(apiSubproject.total_upload_count || 0)
      } else {
        toast.error('Subproject not found')
        setSubproject(null)
      }
    } catch (error: any) {
      console.error('Error fetching subproject:', error)
      toast.error(error?.response?.data?.message || 'Failed to load subproject details')
      setSubproject(null)
    } finally {
      setIsLoading(false)
    }
  }

  // Show skeleton while loading
  if (isLoading && !subproject) {
    return <ProjectDetailSkeleton />
  }

  if (!subproject) {
    return (
      <div className='d-flex flex-column flex-column-fluid'>
        <div className='text-center py-10'>
          <KTIcon iconName='abstract-26' className='fs-3x text-muted mb-5' />
          <h3 className='text-gray-900 mb-3'>Subproject not found</h3>
          <button 
            className='btn btn-primary d-flex align-items-center'
            onClick={() => navigate('/projects/list')}
            style={{ cursor: 'pointer' }}
          >
            <KTIcon iconName='arrow-left' className='fs-2 me-2' />
            <span>Back to Projects</span>
          </button>
        </div>
      </div>
    )
  }

  const getStatusBadge = (status: number | string) => {
    const isActive = status === 1 || status === '1' || status === 'active'
    return (
      <span className={`badge ${isActive ? 'badge-light-success' : 'badge-light-danger'} fw-bold`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    )
  }

  const subprojectName = subproject.subproject_name || subproject.title || 'Untitled'
  const subprojectDescription = subproject.description || ''
  const subprojectStatus = subproject.status || 0

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Helper function to get thumbnail URL from file path
  const getThumbnailUrl = (filePath: string | null | undefined): string | null => {
    if (!filePath) return null;
    
    // If already a full URL, return as is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    
    // Construct full URL from base URL
    let apiBaseUrl = process.env.REACT_APP_API_URL || '';
    // Remove /api suffix if present, but keep the base domain
    apiBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    // Ensure base URL doesn't end with slash
    apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
    
    // Handle file path - remove leading slash if present, then add it back
    const cleanPath = filePath.replace(/^\//, '');
    
    return `${apiBaseUrl}/${cleanPath}`;
  };

  // Helper function to get category badge variant
  const getCategoryBadgeVariant = (categoryName: string | undefined): string => {
    if (!categoryName) return 'secondary';
    const name = categoryName.toLowerCase();
    if (name.includes('video template')) return 'primary';
    if (name.includes('stock video') || name.includes('videos')) return 'success';
    if (name.includes('audio')) return 'warning';
    return 'info';
  };

  // Helper function to format category label
  const getCategoryLabel = (categoryName: string | undefined): string => {
    if (!categoryName) return 'Unknown';
    return categoryName;
  };

  const handleViewAsset = (uploadId: number) => {
    if (canViewAssets && uploadId) {
      navigate(`/digital-assets/view/${uploadId}`);
    }
  };

  const handleDownloadAsset = (upload: any) => {
    if (!upload.asset_file_path || upload.asset_file_path === null || upload.asset_file_path.trim() === '') {
      toast.warning('File is not uploaded yet. Please upload the file first before downloading.');
      return;
    }
    
    const assetData: Asset = {
      id: upload.id,
      name: upload.upload_name || upload.name || '',
      category: upload.category?.category_name 
        ? (upload.category.category_name.toLowerCase().includes('video template') 
            ? AssetCategory.VIDEO_TEMPLATES
            : upload.category.category_name.toLowerCase().includes('stock video')
            ? AssetCategory.STOCK_VIDEOS
            : upload.category.category_name.toLowerCase().includes('audio')
            ? AssetCategory.AUDIOS
            : AssetCategory.GRAPHICS)
        : AssetCategory.GRAPHICS,
      description: upload.description || '',
      tags: upload.tags?.map((t: any) => t.tag_title || t) || [],
      thumbnails: upload.primary_thumbnail?.file_path ? [getThumbnailUrl(upload.primary_thumbnail.file_path) || ''] : [],
      zip_file_url: upload.asset_file_path || '',
      licenses: [],
      metadata: {
        size: 0,
        file_type: 'zip',
        mime_type: 'application/zip',
      },
      source_platform: upload.platform?.platform_name as any,
      uploaded_at: upload.created_at || '',
      status: 'completed',
    };
    
    setSelectedAsset(assetData);
    setShowDownloadModal(true);
  };


  return (
    <div className='d-flex flex-column flex-column-fluid'>
      {/* Loading Progress Bar */}
      <LoadingProgressBar isLoading={isLoading} />
      
      {/* Header */}
      <div className='toolbar' id='kt_toolbar'>
        <div id='kt_toolbar_container' className='container-fluid d-flex flex-stack justify-content-start align-items-start'>
          <div className='page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0 flex-shrink-0'>
            <button 
              className='btn btn-sm btn-light me-3 flex-shrink-0 d-flex align-items-center'
              onClick={() => {
                if (project?.id) {
                  navigate(`/projects/detail/${project.id}`)
                } else {
                  navigate('/projects/list')
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <KTIcon iconName='arrow-left' className='fs-2 me-2' />
              <span>Back</span>
            </button>
            <h1 className='d-flex align-items-center text-dark fw-bolder fs-3 my-1 flex-wrap'>
              <span className='me-2'>{subprojectName}</span>
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div id='kt_content' className='content flex-column-fluid'>
        <div className='container-xxl'>
          {/* Statistics Card */}
          <div className='row g-5 g-xl-8 mb-5'>
            <div className='col-xl-12'>
              <KTCard className='card-xl-stretch mb-xl-8 shadow-sm'>
                <KTCardBody className='d-flex flex-column p-6'>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-60px me-4'>
                      <div className='symbol-label bg-light-info'>
                        <KTIcon iconName='file-down' className='fs-2x text-info' />
                      </div>
                    </div>
                    <div className='flex-grow-1'>
                      <div className='fs-2 fw-bolder text-gray-900 mb-1'>
                        {totalUploadCount}
                      </div>
                      <div className='fs-7 text-muted fw-semibold'>Total Uploads</div>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>
            </div>
          </div>

          {/* Description Tile */}
          {subprojectDescription && (
            <div className='card mb-5'>
              <div className='card-body'>
                <div className='d-flex align-items-center mb-3'>
                  <KTIcon iconName='document' className='fs-2x text-primary me-3' />
                  <h3 className='fw-bolder text-gray-900 mb-0'>Description</h3>
                </div>
                <p className='text-gray-700 fs-6 mb-0' style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {subprojectDescription}
                </p>
              </div>
            </div>
          )}

          {/* Uploads Section */}
          {canViewAssets && (
            <div className='card mb-5'>
              <div className='card-header border-0 pt-6'>
                <div className='card-title'>
                  <h3 className='fw-bolder mb-0'>Associated Uploads</h3>
                </div>
              </div>
              <div className='card-body pt-0'>
                {uploads.length === 0 ? (
                  <div className='text-center py-5'>
                    <KTIcon iconName='picture' className='fs-3x text-muted mb-3' />
                    <p className='text-muted fs-6'>No uploads found for this subproject</p>
                  </div>
                ) : (
                  <div className='row g-4'>
                    {uploads.map((upload: any, index: number) => {
                      // Get thumbnail URL
                      let thumbnailUrl: string | null = null;
                      if (upload.primary_thumbnail?.file_path) {
                        thumbnailUrl = getThumbnailUrl(upload.primary_thumbnail.file_path);
                      }

                      return (
                        <div key={upload.id || index} className='col-md-3 col-lg-3'>
                          <Card 
                            className='h-100 shadow-sm border-0 card-hover' 
                            style={{ transition: 'all 0.3s ease', cursor: canViewAssets ? 'pointer' : 'default' }}
                            onClick={canViewAssets ? () => handleViewAsset(upload.id) : undefined}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-5px)';
                              e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                            }}
                          >
                            <div className='position-relative'>
                              {thumbnailUrl ? (
                                <Card.Img
                                  variant='top'
                                  src={thumbnailUrl}
                                  style={{ height: '220px', objectFit: 'cover' }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23e0e0e0" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" font-size="18" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                              ) : (
                                <div 
                                  className='d-flex align-items-center justify-content-center bg-light'
                                  style={{ height: '220px' }}
                                >
                                  <KTIcon iconName='picture' className='fs-1 text-muted' />
                                </div>
                              )}
                              {/* Status Badge */}
                              <div className='position-absolute top-0 end-0 m-2'>
                                {upload.asset_file_path && upload.asset_file_path !== null && upload.asset_file_path.trim() !== '' ? (
                                  <Badge bg='success' className='px-2 py-1'>
                                    <KTIcon iconName='check-circle' className='me-1 fs-7' />
                                    Ready
                                  </Badge>
                                ) : (
                                  <Badge bg='warning' className='px-2 py-1'>
                                    <KTIcon iconName='clock' className='me-1 fs-7' />
                                    Pending
                                  </Badge>
                                )}
                              </div>
                              {/* Category Badge */}
                              {upload.category?.category_name && (
                                <div className='position-absolute top-0 start-0 m-2'>
                                  <Badge bg={getCategoryBadgeVariant(upload.category.category_name)} className='px-2 py-1'>
                                    {getCategoryLabel(upload.category.category_name)}
                                  </Badge>
                                </div>
                              )}
                            </div>
                            <Card.Body className='d-flex flex-column p-3'>
                              <Card.Title className='fs-6 fw-bold mb-2 text-truncate' title={upload.upload_name || upload.name}>
                                {upload.upload_name || upload.name}
                              </Card.Title>
                              
                              {/* Description Preview */}
                              {upload.description && (
                                <Card.Text className='text-muted small mb-2' style={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  minHeight: '40px'
                                }}>
                                  {upload.description}
                                </Card.Text>
                              )}

                              {/* Platform Badge */}
                              {upload.platform?.platform_name && (
                                <div className='mb-2'>
                                  <Badge bg='info' className='px-2 py-1'>
                                    <KTIcon iconName='link' className='me-1 fs-8' />
                                    {upload.platform.platform_name}
                                  </Badge>
                                </div>
                              )}

                              {/* License Date */}
                              {upload.pivot?.license_date && (
                                <div className='mb-2'>
                                  <div className='d-flex align-items-center text-muted small'>
                                    <KTIcon iconName='calendar' className='me-1 fs-7' />
                                    <span>License: {formatDate(upload.pivot.license_date)}</span>
                                  </div>
                                </div>
                              )}

                              {/* Date */}
                              <div className='mb-2'>
                                <div className='d-flex align-items-center text-muted small'>
                                  <KTIcon iconName='calendar' className='me-1 fs-7' />
                                  <span>{formatDate(upload.created_at)}</span>
                                </div>
                              </div>

                              {/* Tags */}
                              {upload.tags && upload.tags.length > 0 && (
                                <div className='mb-2'>
                                  {upload.tags.slice(0, 2).map((tag: any, tagIndex: number) => (
                                    <Badge key={tagIndex} bg='secondary' className='me-1 mb-1' style={{ fontSize: '10px' }}>
                                      {tag.tag_title || tag}
                                    </Badge>
                                  ))}
                                  {upload.tags.length > 2 && (
                                    <Badge bg='secondary' style={{ fontSize: '10px' }}>
                                      +{upload.tags.length - 2}
                                    </Badge>
                                  )}
                                </div>
                              )}

                              {/* Action Buttons */}
                              <div className='d-flex gap-1 mt-auto pt-2 border-top' onClick={(e) => e.stopPropagation()}>
                                {canViewAssets && (
                                  <Button
                                    variant='primary'
                                    size='sm'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewAsset(upload.id);
                                    }}
                                    className='flex-fill'
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                  >
                                    <KTIcon iconName='eye' className='me-1 fs-7' />
                                    View
                                  </Button>
                                )}
                                {canDownloadAsset && upload.asset_file_path && upload.asset_file_path.trim() !== '' && (
                                  <Button
                                    variant='info'
                                    size='sm'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadAsset(upload);
                                    }}
                                    title='Download Asset'
                                    className='flex-fill'
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                  >
                                    <KTIcon iconName='file-down' className='me-1 fs-7' />
                                    Download
                                  </Button>
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div className='card mb-5'>
            <div className='card-header border-0 pt-6'>
              <div className='card-title'>
                <h3 className='fw-bolder mb-0'>Additional Information</h3>
              </div>
            </div>
            <div className='card-body pt-0'>
              <div className='row g-4'>
                <div className='col-md-6'>
                  <div className='d-flex flex-column'>
                    <span className='text-muted fs-7 mb-1'>Status</span>
                    <span className='text-gray-900 fw-bold'>
                      {getStatusBadge(subprojectStatus)}
                    </span>
                  </div>
                </div>
                {subproject.updated_at && (
                  <div className='col-md-6'>
                    <div className='d-flex flex-column'>
                      <span className='text-muted fs-7 mb-1'>Updated At</span>
                      <span className='text-gray-900 fw-bold'>
                        {new Date(subproject.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Download Modal */}
      <AssetDownloadModal
        show={showDownloadModal}
        onHide={() => {
          setShowDownloadModal(false);
          setSelectedAsset(null);
        }}
        asset={selectedAsset}
      />
    </div>
  )
}

export {SubprojectDetail}

